import { routes } from '@/pages'
import { Button, Text } from '@/ui-kit'
import { Trans } from '@lingui/react/macro'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSubmitMissionResult } from '../api'
import type { Mission } from '../api/models'
import { useMissionsUrl } from '../context/MissionsUrlProvider'
import FileUploader from './FileUploader'
import { MissionDemographicsForm } from './MissionDemographicsForm'
import { MissionExternalActionForm } from './MissionExternalActionForm'
import { MissionNotAvailable } from './MissionNotAvailable'

type MissionContentProps = {
  mission: Mission
  isAuthorized?: boolean
}

export const MissionContent = ({ mission, isAuthorized = false }: MissionContentProps) => {
  const { missionsUrl } = useMissionsUrl()
  const location = useLocation()

  const { mutate: submitMissionResult } = useSubmitMissionResult()
  const navigate = useNavigate()

  const goToLogin = () => {
    const returnTo = `${location.pathname}${location.search}`
    navigate(`${routes.login.path}?redirect=${encodeURIComponent(returnTo)}`)
  }

  const onUploadSuccess = () => {
    navigate(missionsUrl)
  }

  const onCheckAuthorization = () => {
    if (mission) {
      submitMissionResult({ missionId: mission.id })
    }
  }

  if (!mission.availability.available) {
    return (
      <MissionNotAvailable
        projectId={mission.id}
        title={mission.title}
        description={mission.shortDescription ?? ''}
        rewardConfig={{ type: 'points', amount: mission.reward }}
        isAuthorized={isAuthorized}
      />
    )
  }

  return (
    <div className="flex max-w-[845px] flex-col gap-6 py-6">
      {mission.description.sections?.map((section, index) => (
        <div key={`${mission.id}-section-${index}-${section.name}`} className="flex flex-col gap-2">
          <Text type="subtitle-semibold" className="text-gray-70">
            {section.name}
          </Text>
          <div
            dangerouslySetInnerHTML={{ __html: section.body }}
            className="quill-editor text-gray-80 [&_hover:a]:text-orange [&_li]:my-1 [&_ol]:my-2 [&_ol]:ml-6 [&_ol]:list-decimal [&_ul]:my-2 [&_ul]:ml-8 [&_ul]:list-disc"
          />
          {section.image && <img src={section.image} alt={section.name} className="mt-2 rounded-lg" />}
        </div>
      ))}

      {!isAuthorized ? (
        <Button onClick={goToLogin} className="max-mobile:w-full max-mobile:max-w-full max-w-[300px]">
          <Trans>Sign In</Trans>
        </Button>
      ) : mission.view.latestSubmissionStatus === 'SUBMITTED' ? (
        <Button isDisabled={true} className="max-mobile:w-full max-mobile:max-w-full max-w-[300px]">
          <Trans>Validating...</Trans>
        </Button>
      ) : (
        <>
          {mission.type === 'FILE_UPLOAD' && mission.view.nextSubmissionAvailable && mission.status !== 'ENDED' && (
            <FileUploader
              missionId={mission.id}
              buttonText={mission.view.buttonText}
              onUploadSuccess={onUploadSuccess}
            />
          )}
          {mission.type === 'WEBSITE_LOGIN' && mission.view.nextSubmissionAvailable && mission.status !== 'ENDED' && (
            <Button onClick={onCheckAuthorization} className="max-mobile:w-full max-mobile:max-w-full max-w-[300px]">
              <Trans>Check authorization</Trans>
            </Button>
          )}
          {mission.type === 'EXTERNAL_ACTION' && mission.view.nextSubmissionAvailable && mission.status !== 'ENDED' && (
            <MissionExternalActionForm mission={mission} />
          )}
          {mission.type === 'PROFILE' && mission.status !== 'ENDED' && <MissionDemographicsForm mission={mission} />}
        </>
      )}
    </div>
  )
}
